/**
 * DEV TOOL — the folder view's font dropdown. Ships disabled.
 *
 * A dropdown in the folder view header that re-fonts the whole app at
 * runtime, so candidates can be judged in the real product instead of in a
 * mock. It is safe to merge in this state and costs production nothing:
 * FONT_PREVIEW_ENABLED is false, so the control does not render and a stored
 * choice does not count; the four comparison faces are not imported, so they
 * add no build weight; and the remaining options (Nunito Sans at three widths,
 * Roboto) are faces the app ships regardless.
 *
 * It stays because the font question is not finished — the `useRobotoFont` config
 * gives orgs an opt-out, and this is how that choice gets judged on real screens.
 * Delete this file and grep for `fontPreview` when it is.
 *
 * It is a SUPERSET of what ships. The shippable list is `UI_FONT_CHOICES` in
 * `@common/styles/fonts` — three options; the shipping config,
 * `useRobotoFont`, only picks between the first and the last. The extra families here exist only to compare against, and their
 * packages come out with this file.
 *
 * Two things to know while it lives:
 *
 * 1. A local choice OUTRANKS the organization's `useRobotoFont` config, so switching
 *    here survives a reload — see `hasDevFontOverride`, which `UiFontSync`
 *    asks about. Clearing `localStorage` hands control back to the org.
 *
 * 2. It deliberately CANNOT reach `BURNED_IN_TEXT_FONT_FAMILY`. Overlay text
 *    and redaction codes are rendered into customer evidence and stay pinned
 *    to Roboto. If a per-user font preference reached them, two operators
 *    with different settings would produce differently-positioned text in the
 *    same output.
 */

import { APP_FONT_FAMILY } from '@common/styles/fonts';

/** The CSS custom property every stylesheet and every MUI theme reads. */
export const FONT_PREVIEW_VAR = '--app-font-family';

/**
 * The master switch. OFF hides the dropdown AND stops a previously stored
 * choice counting, so the organization's `useRobotoFont` config decides on its own —
 * which is the point: a leftover localStorage value would otherwise keep
 * outranking the config and make it look like the config does nothing.
 *
 * The stored value is left in place rather than cleared, so flipping this
 * back to `true` restores whatever was selected.
 */
export const FONT_PREVIEW_ENABLED = false;

/**
 * Whether the four non-Nunito faces are actually loaded. They are NOT, by
 * default: their imports in bootstrap.tsx are commented out because webpack
 * emits 596KB of .woff2 for them whether or not anything renders in them.
 *
 * Flip this AND uncomment that block to compare them — both, or the options
 * appear and silently render in the system sans, which reads as a bug rather
 * than a missing import. The packages stay in package.json so no install is
 * needed.
 */
export const EXTRA_PREVIEW_FONTS_LOADED = false;

const STORAGE_KEY = 'redact.fontPreview';

export interface FontPreviewOption {
  readonly id: string;
  readonly label: string;
  readonly stack: string;
  /** Shown beside the label, e.g. to mark the shipping default. */
  readonly note?: string;
  /**
   * Width axis, for a face that has one. Nunito Sans Variable carries `wdth`
   * 75%-125%, so it can be condensed without changing typeface.
   *
   * Caveat: this narrows the DOM, but `ctx.font` cannot express a percentage
   * stretch, so the canvas measuring sites keep measuring at 100%. They
   * over-estimate, which errs generous in all three places.
   */
  readonly stretch?: string;
  /** Needs the commented-out bootstrap.tsx imports — see EXTRA_PREVIEW_FONTS_LOADED. */
  readonly needsExtraImport?: boolean;
}

/**
 * Each stack leads with the family name the @fontsource package actually
 * REGISTERS, which is not always the human name — the variable packages
 * append "Variable". Getting this wrong fails silently: the browser skips the
 * unknown family and renders the next one, which is how this app previously
 * ended up with ~27 Nunito declarations that never rendered anything.
 */
export const FONT_PREVIEW_OPTIONS: readonly FontPreviewOption[] = [
  {
    id: 'nunitoSans',
    label: 'Nunito Sans',
    note: 'default',
    stack: APP_FONT_FAMILY,
  },
  {
    id: 'nunitoSans95',
    label: 'Nunito Sans 95%',
    note: 'condensed',
    stack: APP_FONT_FAMILY,
    stretch: '95%',
  },
  {
    id: 'nunitoSans90',
    label: 'Nunito Sans 90%',
    stack: APP_FONT_FAMILY,
    stretch: '90%',
  },
  {
    id: 'inter',
    needsExtraImport: true,
    label: 'Inter',
    stack: '"Inter Variable", "Inter", ui-sans-serif, system-ui, sans-serif',
  },
  {
    id: 'publicSans',
    needsExtraImport: true,
    label: 'Public Sans',
    stack:
      '"Public Sans Variable", "Public Sans", ui-sans-serif, system-ui, sans-serif',
  },
  {
    id: 'ibmPlexSans',
    needsExtraImport: true,
    label: 'IBM Plex Sans',
    stack: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
  },
  {
    id: 'sourceSans3',
    needsExtraImport: true,
    label: 'Source Sans 3',
    stack:
      '"Source Sans 3 Variable", "Source Sans 3", ui-sans-serif, system-ui, sans-serif',
  },
  {
    id: 'roboto',
    label: 'Roboto',
    note: 'before',
    stack: 'Roboto, Arial, Helvetica, sans-serif',
  },
];

/** What the dropdown may offer: everything whose face is actually loaded. */
export const visibleFontPreviewOptions = (): readonly FontPreviewOption[] =>
  FONT_PREVIEW_OPTIONS.filter(
    (o) => EXTRA_PREVIEW_FONTS_LOADED || !o.needsExtraImport
  );

/**
 * Folder view row size. Separate from the font because they trade against
 * each other: a bigger size reads better and costs width, condensing buys
 * width back. Worth being able to move one without the other.
 */
export const ROW_SIZE_OPTIONS: readonly { id: string; label: string }[] = [
  { id: '12px', label: 'Rows 12px' },
  { id: '13px', label: 'Rows 13px' },
  { id: '14px', label: 'Rows 14px' },
];

const ROW_SIZE_KEY = 'redact.fontPreviewRowSize';
const DEFAULT_ROW_SIZE = '13px';

export const getRowSizeId = (): string => {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(ROW_SIZE_KEY);
  } catch {
    stored = null;
  }
  return ROW_SIZE_OPTIONS.some((o) => o.id === stored)
    ? (stored as string)
    : DEFAULT_ROW_SIZE;
};

export const applyRowSize = (id: string): void => {
  const option = ROW_SIZE_OPTIONS.find((o) => o.id === id);
  if (!option) {
    return;
  }
  document.documentElement.style.setProperty('--app-row-font-size', option.id);
  try {
    window.localStorage.setItem(ROW_SIZE_KEY, option.id);
  } catch {
    // Not worth failing over.
  }
};

const DEFAULT_ID = FONT_PREVIEW_OPTIONS[0]?.id ?? 'nunitoSans';

const readStoredId = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private windows and blocked site data both throw here.
    return null;
  }
};

/**
 * Whether a local choice has been made. The organization's config
 * defers to it, so switching in the dropdown survives a reload.
 */
export const hasDevFontOverride = (): boolean =>
  FONT_PREVIEW_ENABLED && readStoredId() !== null;

export const getFontPreviewId = (): string => {
  const stored = readStoredId();
  // Validated against the VISIBLE list, not all of them: a choice stored while
  // the extra faces were loaded would otherwise come back after they are
  // switched off and render in the system sans.
  return visibleFontPreviewOptions().some((o) => o.id === stored)
    ? (stored as string)
    : DEFAULT_ID;
};

/**
 * Point the custom property at the chosen stack. Stylesheets and the MUI
 * themes both follow it. Canvas text measurement does NOT — the measuring
 * sites call `resolveAppFont` in `@common/styles/fonts` instead.
 */
export const applyFontPreview = (id: string): void => {
  const option =
    FONT_PREVIEW_OPTIONS.find((o) => o.id === id) ?? FONT_PREVIEW_OPTIONS[0];
  if (!option) {
    return;
  }
  document.documentElement.style.setProperty(FONT_PREVIEW_VAR, option.stack);
  // font-stretch inherits, so setting it on the root reaches everything.
  document.documentElement.style.fontStretch = option.stretch ?? '100%';
  try {
    window.localStorage.setItem(STORAGE_KEY, option.id);
  } catch {
    // Not being able to remember the choice is not worth failing over.
  }
};

/**
 * Re-apply a remembered choice on boot, so a reload keeps the same font.
 * Does nothing without one, leaving the org's config to decide.
 */
export const applyStoredFontPreview = (): void => {
  if (FONT_PREVIEW_ENABLED && hasDevFontOverride()) {
    applyFontPreview(getFontPreviewId());
  }
  if (FONT_PREVIEW_ENABLED) {
    applyRowSize(getRowSizeId());
  }
};
